import React, { useState } from "react";
import { Leaf, Sprout, TreeDeciduous, Download, BookOpen, Package, ArrowRight, CheckCircle, Heart, Sparkles, Mail, Star } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { API_BASE_URL } from "../config/api";

const API_URL = API_BASE_URL;

/**
 * SEED GROWTH COLOR THEME
 * Representing the journey from dark soil to light (growth)
 * 
 * Dark Soil (Bottom/Start): #0e3030
 * Deep Teal: #245137
 * Forest Green: #3a6c2b
 * Growth Green: #5c8a1c
 * Vibrant Green: #84a72b
 * Lime: #a9c24d
 * Light Lime: #cddc7a
 * Light Yellow (Top/End): #edf5ac
 */

const ColoringBookFunnel = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [activeStage, setActiveStage] = useState("seed"); // seed, digital, print

  const handleSeedDownload = async (e) => {
    e.preventDefault();
    if (!email || !name) return;
    
    setIsSubmitting(true);
    
    try {
      // Save subscriber to database
      const response = await fetch(`${API_URL}/api/subscribers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          name,
          source: 'coloring_book_seed',
          consent_given: true
        })
      });
      
      setSubmitted(true);
      
      // Trigger the actual PDF download
      const downloadLink = document.createElement('a');
      downloadLink.href = '/downloads/seedz-shy-yahffirmations.pdf';
      downloadLink.download = 'SEEDZ-SHY-YAHFFIRMATIONS-by-Keeper-Of-The-Fiyah.pdf';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      
    } catch (error) {
      console.error('Error:', error);
      // Still allow download even if subscription fails
      setSubmitted(true);
      const downloadLink = document.createElement('a');
      downloadLink.href = '/downloads/seedz-shy-yahffirmations.pdf';
      downloadLink.download = 'SEEDZ-SHY-YAHFFIRMATIONS-by-Keeper-Of-The-Fiyah.pdf';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Seed Stage - Free Download (Lead Magnet) - DARK SOIL THEME
  const SeedStage = () => (
    <div className="relative">
      {/* Hero Section */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm mb-6" style={{backgroundColor: 'rgba(58, 108, 43, 0.3)', color: '#a9c24d'}}>
          <Leaf className="w-4 h-4" />
          <span>FREE SEED ARTIFACT</span>
        </div>
        
        <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
          Plant Your <span style={{color: '#84a72b'}}>Seed</span>
        </h1>
        
        <p className="text-xl mb-2" style={{color: '#cddc7a'}}>
          BOOK OF COLORZ: SHY YAHFFIRMATIONS
        </p>
        
        <p className="text-lg mb-4 italic" style={{color: '#a9c24d'}}>
          "She is Her, She is You, Executing as the BUTTERFLY"
        </p>
        
        <p className="max-w-xl mx-auto" style={{color: '#6b9b6b'}}>
          Begin your divine connection journey with this sacred teaser - 
          7 curated coloring pages designed to INSPIYAH generations through YAH's Mother Nature Healing Plan.
        </p>
      </div>

      {/* Preview Image */}
      <div className="flex justify-center mb-12">
        <div className="relative">
          <div className="w-72 h-96 rounded-lg flex items-center justify-center border-2 overflow-hidden" 
               style={{
                 background: 'linear-gradient(to bottom, #3a6c2b, #245137, #0e3030)',
                 borderColor: 'rgba(169, 194, 77, 0.3)'
               }}>
            <div className="text-center p-6">
              {/* Black Rose Icon representation */}
              <div className="relative mb-4">
                <svg className="w-20 h-20 mx-auto" viewBox="0 0 100 100" fill="none">
                  <path d="M50 20 C30 35, 25 55, 50 70 C75 55, 70 35, 50 20" fill="#1a1a1a" stroke="#14b8a6" strokeWidth="1"/>
                  <path d="M35 30 C25 45, 30 60, 50 65" fill="none" stroke="#14b8a6" strokeWidth="1"/>
                  <path d="M65 30 C75 45, 70 60, 50 65" fill="none" stroke="#14b8a6" strokeWidth="1"/>
                  <path d="M50 70 L50 95" stroke="#1a1a1a" strokeWidth="3"/>
                  <path d="M50 70 L50 95" stroke="#14b8a6" strokeWidth="1"/>
                  <path d="M50 80 C40 75, 35 80, 40 85 C45 82, 50 80, 50 80" fill="#1a1a1a" stroke="#14b8a6" strokeWidth="0.5"/>
                  <path d="M50 85 C60 80, 65 85, 60 90 C55 87, 50 85, 50 85" fill="#1a1a1a" stroke="#14b8a6" strokeWidth="0.5"/>
                </svg>
              </div>
              <p className="font-semibold text-lg" style={{color: '#a9c24d'}}>THE SEED</p>
              <p className="text-sm mt-2" style={{color: '#cddc7a'}}>SHY YAHFFIRMATIONS</p>
              <p className="text-xs mt-2 italic" style={{color: '#84a72b'}}>by Keeper Of The Fiyah ©™</p>
            </div>
          </div>
          <div className="absolute -top-4 -right-4 w-8 h-8 rounded-full animate-pulse" style={{backgroundColor: 'rgba(92, 138, 28, 0.3)'}}></div>
          <div className="absolute -bottom-4 -left-4 w-6 h-6 rounded-full animate-pulse" style={{backgroundColor: 'rgba(58, 108, 43, 0.4)', animationDelay: '300ms'}}></div>
        </div>
      </div>

      {/* 7 Sections Preview */}
      <div className="max-w-3xl mx-auto mb-12">
        <h3 className="font-semibold text-center mb-6" style={{color: '#a9c24d'}}>7 SACRED SECTIONS INSIDE</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { title: "SHY, YOU", verse: "Isaiah 43:10" },
            { title: "FLAME", verse: "Song of Solomon 8:6" },
            { title: "REMEMBER THE SHY", verse: "2 Kings 20:3" },
            { title: "EYE TOUCH LOVE", verse: "1 John 4:8" },
            { title: "TRIBE TO INSPIYAH", verse: "Revelation 5:5" },
            { title: "YAHAWAH", verse: "Isaiah 43:11" },
            { title: "YHWH יהוה IS THE WAY", verse: "Isaiah 38:16" },
            { title: "+ BONUS", verse: "Dedication" }
          ].map((item, idx) => (
            <div key={idx} className="rounded-lg p-3 text-center border" 
                 style={{
                   backgroundColor: 'rgba(36, 81, 55, 0.3)',
                   borderColor: 'rgba(92, 138, 28, 0.2)'
                 }}>
              <p className="text-xs font-semibold" style={{color: '#cddc7a'}}>{item.title}</p>
              <p className="text-xs mt-1" style={{color: '#5c8a1c'}}>{item.verse}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Scripture Quote */}
      <div className="max-w-2xl mx-auto mb-12 text-center px-4">
        <blockquote className="italic text-lg" style={{color: '#cddc7a'}}>
          "Place me as a seal upon your heart... for love is as strong as death, 
          its flames are a blazing fire, the flame of YAH."
        </blockquote>
        <p className="text-sm mt-2" style={{color: '#5c8a1c'}}>— Song of Solomon 8:6</p>
      </div>

      {/* Email Capture Form */}
      {!submitted ? (
        <div className="max-w-md mx-auto">
          <div className="rounded-2xl p-8 border" 
               style={{
                 background: 'linear-gradient(to bottom right, rgba(36, 81, 55, 0.4), rgba(14, 48, 48, 0.6))',
                 borderColor: 'rgba(92, 138, 28, 0.3)'
               }}>
            <h3 className="text-white text-xl font-semibold text-center mb-2">
              Plant Your Seed Today
            </h3>
            <p className="text-sm text-center mb-2" style={{color: '#a9c24d'}}>
              For the Wise & Capable Woman
            </p>
            <p className="text-sm text-center mb-6" style={{color: '#6b9b6b'}}>
              Initiate the Flame of YAH with your free coloring pages
            </p>
            
            <form onSubmit={handleSeedDownload} className="space-y-4">
              <Input
                type="text"
                placeholder="Your Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="border text-white placeholder:text-gray-500"
                style={{backgroundColor: 'rgba(14, 48, 48, 0.6)', borderColor: 'rgba(92, 138, 28, 0.3)'}}
                required
              />
              <Input
                type="email"
                placeholder="Your Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border text-white placeholder:text-gray-500"
                style={{backgroundColor: 'rgba(14, 48, 48, 0.6)', borderColor: 'rgba(92, 138, 28, 0.3)'}}
                required
              />
              
              <Button 
                type="submit" 
                className="w-full font-semibold py-6 text-black"
                style={{backgroundColor: '#84a72b'}}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  "Planting..."
                ) : (
                  <>
                    <Leaf className="w-5 h-5 mr-2" />
                    PLANT THE SEED - FREE DOWNLOAD
                  </>
                )}
              </Button>
            </form>
            
            <p className="text-xs text-center mt-4" style={{color: '#3a6c2b'}}>
              Join the tribe. Receive YAHFFIRMATIONS and updates. 
              Unsubscribe anytime.
            </p>
          </div>
        </div>
      ) : (
        <div className="max-w-md mx-auto">
          <div className="rounded-2xl p-8 text-center border"
               style={{
                 background: 'linear-gradient(to bottom right, rgba(92, 138, 28, 0.2), rgba(14, 48, 48, 0.4))',
                 borderColor: 'rgba(132, 167, 43, 0.3)'
               }}>
            <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{color: '#a9c24d'}} />
            <h3 className="text-white text-xl font-semibold mb-2">
              Your Seed Has Been Planted!
            </h3>
            <p className="mb-4" style={{color: '#6b9b6b'}}>
              Your download should start automatically. 
              If not, click the button below.
            </p>
            
            {/* Download Again Button */}
            <a 
              href="/downloads/seedz-shy-yahffirmations.pdf"
              download="SEEDZ-SHY-YAHFFIRMATIONS-by-Keeper-Of-The-Fiyah.pdf"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold mb-6 transition-all hover:scale-105"
              style={{backgroundColor: '#5c8a1c', color: 'white'}}
            >
              <Download className="w-5 h-5" />
              Download Seedz PDF
            </a>
            
            <p className="mb-6" style={{color: '#84a72b'}}>
              Now it's time to water the seed...
            </p>
            <Button 
              onClick={() => setActiveStage("digital")}
              className="font-semibold text-black"
              style={{backgroundColor: '#84a72b'}}
            >
              <Sprout className="w-5 h-5 mr-2" />
              WATER THE SEED
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Journey Preview */}
      <div className="mt-16 text-center">
        <p className="text-sm mb-4" style={{color: '#3a6c2b'}}>YOUR DIVINE CONNECTION JOURNEY</p>
        <div className="flex justify-center items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2" style={{color: '#84a72b'}}>
            <Leaf className="w-5 h-5" />
            <span className="text-sm font-semibold">PLANT</span>
          </div>
          <ArrowRight className="w-4 h-4" style={{color: '#245137'}} />
          <div className="flex items-center gap-2" style={{color: '#3a6c2b'}}>
            <Sprout className="w-5 h-5" />
            <span className="text-sm">WATER</span>
          </div>
          <ArrowRight className="w-4 h-4" style={{color: '#245137'}} />
          <div className="flex items-center gap-2" style={{color: '#3a6c2b'}}>
            <TreeDeciduous className="w-5 h-5" />
            <span className="text-sm">GROW</span>
          </div>
        </div>
      </div>
    </div>
  );

  // Digital Stage - Paid Digital Download - MID GREEN THEME
  const DigitalStage = () => (
    <div className="relative">
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm mb-6" 
             style={{backgroundColor: 'rgba(92, 138, 28, 0.3)', color: '#cddc7a'}}>
          <Sprout className="w-4 h-4" />
          <span>WATER THE SEED</span>
        </div>
        
        <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
          Nurture Your <span style={{color: '#a9c24d'}}>Growth</span>
        </h1>
        
        <p className="text-xl mb-2" style={{color: '#cddc7a'}}>
          The Complete Digital Coloring Book
        </p>
        
        <p className="max-w-xl mx-auto" style={{color: '#84a72b'}}>
          You've planted the seed. Now water it with the full collection of 
          sacred designs, meditative patterns, and divine affirmations.
        </p>
      </div>

      {/* Product Preview */}
      <div className="flex justify-center mb-12">
        <div className="relative">
          <div className="w-80 h-[28rem] rounded-lg flex items-center justify-center border-2"
               style={{
                 background: 'linear-gradient(to bottom, #5c8a1c, #3a6c2b, #245137)',
                 borderColor: 'rgba(169, 194, 77, 0.4)'
               }}>
            <div className="text-center p-6">
              <BookOpen className="w-20 h-20 mx-auto mb-4" style={{color: '#cddc7a'}} />
              <p className="font-semibold text-lg" style={{color: '#edf5ac'}}>FULL COLORING BOOK</p>
              <p className="mt-2" style={{color: '#a9c24d'}}>Digital Download</p>
              <div className="mt-4 text-3xl font-bold" style={{color: '#edf5ac'}}>$22.22</div>
              <p className="text-xs mt-1" style={{color: '#84a72b'}}>Sacred Numerology Pricing</p>
            </div>
          </div>
        </div>
      </div>

      {/* What's Included */}
      <div className="max-w-3xl mx-auto mb-12">
        <h3 className="font-semibold text-center mb-6" style={{color: '#a9c24d'}}>WHAT'S INCLUDED</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            "30+ Sacred Designs",
            "Meditative Patterns",
            "YAHFFIRMATIONS Pages",
            "Printable PDF Format",
            "Scripture Reflections",
            "Bonus Devotionals",
            "Instant Download",
            "Lifetime Access"
          ].map((item, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 flex-shrink-0" style={{color: '#a9c24d'}} />
              <span className="text-sm" style={{color: '#cddc7a'}}>{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-md mx-auto text-center">
        <Button 
          className="font-semibold py-6 px-8 text-lg text-black"
          style={{backgroundColor: '#a9c24d'}}
          onClick={() => alert('Stripe integration coming soon!')}
        >
          <Download className="w-5 h-5 mr-2" />
          WATER THE SEED - $22.22
        </Button>
        <p className="text-sm mt-4" style={{color: '#5c8a1c'}}>
          Instant digital download • Print unlimited copies
        </p>
        
        <div className="mt-8">
          <Button 
            variant="outline"
            onClick={() => setActiveStage("print")}
            className="hover:bg-opacity-10"
            style={{borderColor: 'rgba(205, 220, 122, 0.5)', color: '#cddc7a'}}
          >
            Ready to GROW? See the Print Edition
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Journey Progress */}
      <div className="mt-16 text-center">
        <p className="text-sm mb-4" style={{color: '#3a6c2b'}}>YOUR DIVINE CONNECTION JOURNEY</p>
        <div className="flex justify-center items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2" style={{color: '#5c8a1c'}}>
            <Leaf className="w-5 h-5" />
            <span className="text-sm">PLANTED</span>
            <CheckCircle className="w-4 h-4" style={{color: '#a9c24d'}} />
          </div>
          <ArrowRight className="w-4 h-4" style={{color: '#3a6c2b'}} />
          <div className="flex items-center gap-2" style={{color: '#a9c24d'}}>
            <Sprout className="w-5 h-5" />
            <span className="text-sm font-semibold">WATER</span>
          </div>
          <ArrowRight className="w-4 h-4" style={{color: '#3a6c2b'}} />
          <div className="flex items-center gap-2" style={{color: '#5c8a1c'}}>
            <TreeDeciduous className="w-5 h-5" />
            <span className="text-sm">GROW</span>
          </div>
        </div>
      </div>
    </div>
  );

  // Print Stage - Physical Book - LIGHT/GROWTH THEME with Black Rose Backdrop
  const PrintStage = () => (
    <div className="relative">
      {/* Black Rose Backdrop Image */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 flex items-center justify-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_portfolio-cms-18/artifacts/9bnb06ww_seed%20water%20grow.png" 
            alt="Growth backdrop"
            className="w-full h-full object-cover opacity-60"
          />
        </div>
        {/* Subtle gradient overlay for text readability */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(to bottom, rgba(132, 167, 43, 0.3), rgba(92, 138, 28, 0.2), rgba(58, 108, 43, 0.4))'
        }}></div>
      </div>

      {/* Content - positioned above backdrop */}
      <div className="relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm mb-6"
               style={{backgroundColor: 'rgba(169, 194, 77, 0.3)', color: '#edf5ac'}}>
            <TreeDeciduous className="w-4 h-4" />
            <span>WATCH IT GROW</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            Commit to <span style={{color: '#edf5ac'}}>Growth</span>
          </h1>
          
          <p className="text-xl mb-2" style={{color: '#edf5ac'}}>
            The Premium Print Edition
          </p>
          
          <p className="max-w-xl mx-auto" style={{color: '#cddc7a'}}>
            A physical testament to your divine connection journey. 
            Premium paper, beautiful binding, a keepsake for generations.
          </p>
        </div>

        {/* Product Preview */}
        <div className="flex justify-center mb-12">
          <div className="relative">
            <div className="w-80 h-[28rem] rounded-lg flex items-center justify-center border-2 backdrop-blur-sm"
                 style={{
                   background: 'linear-gradient(to bottom, rgba(169, 194, 77, 0.9), rgba(132, 167, 43, 0.9), rgba(92, 138, 28, 0.9))',
                   borderColor: 'rgba(237, 245, 172, 0.5)'
                 }}>
              <div className="text-center p-6">
                <Package className="w-20 h-20 mx-auto mb-4" style={{color: '#0e3030'}} />
                <p className="font-semibold text-lg" style={{color: '#0e3030'}}>PRINT EDITION</p>
                <p className="mt-2" style={{color: '#245137'}}>Premium Physical Book</p>
                <div className="mt-4 text-3xl font-bold" style={{color: '#0e3030'}}>$44.44</div>
                <p className="text-xs mt-1" style={{color: '#245137'}}>Includes Free Digital Download</p>
              </div>
            </div>
            {/* Premium badge */}
            <div className="absolute -top-3 -right-3 text-xs font-bold px-3 py-1 rounded-full"
                 style={{backgroundColor: '#edf5ac', color: '#0e3030'}}>
              PREMIUM
            </div>
          </div>
        </div>

        {/* What's Included */}
        <div className="max-w-3xl mx-auto mb-12">
          <h3 className="font-semibold text-center mb-6" style={{color: '#cddc7a'}}>THE COMPLETE EXPERIENCE</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              "Premium Paper Quality",
              "Professional Binding",
              "30+ Sacred Designs",
              "Scripture Reflections",
              "FREE Digital Version",
              "Gift-Ready Packaging",
              "Shipped to Your Door",
              "Heirloom Quality"
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 flex-shrink-0" style={{color: '#cddc7a'}} />
                <span className="text-sm" style={{color: '#edf5ac'}}>{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="max-w-md mx-auto text-center">
          <Button 
            className="font-semibold py-6 px-8 text-lg shadow-xl"
            style={{backgroundColor: '#edf5ac', color: '#0e3030'}}
            onClick={() => alert('Print ordering coming soon!')}
          >
            <Package className="w-5 h-5 mr-2" />
            GROW THE SEED - $44.44
          </Button>
          <p className="text-sm mt-4" style={{color: '#84a72b'}}>
            Free shipping • Includes digital download • Gift-ready
          </p>
        </div>

        {/* Journey Complete */}
        <div className="mt-16 text-center">
          <p className="text-sm mb-4" style={{color: '#5c8a1c'}}>YOUR DIVINE CONNECTION JOURNEY</p>
          <div className="flex justify-center items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2" style={{color: '#84a72b'}}>
              <Leaf className="w-5 h-5" />
              <span className="text-sm">PLANTED</span>
              <CheckCircle className="w-4 h-4" style={{color: '#a9c24d'}} />
            </div>
            <ArrowRight className="w-4 h-4" style={{color: '#5c8a1c'}} />
            <div className="flex items-center gap-2" style={{color: '#a9c24d'}}>
              <Sprout className="w-5 h-5" />
              <span className="text-sm">WATERED</span>
              <CheckCircle className="w-4 h-4" style={{color: '#cddc7a'}} />
            </div>
            <ArrowRight className="w-4 h-4" style={{color: '#84a72b'}} />
            <div className="flex items-center gap-2" style={{color: '#edf5ac'}}>
              <TreeDeciduous className="w-5 h-5" />
              <span className="text-sm font-semibold">GROW</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Dynamic background based on stage - Soil to Light gradient
  const getStageBackground = () => {
    switch(activeStage) {
      case "seed":
        return 'linear-gradient(to bottom, #245137, #0e3030)'; // Dark soil
      case "digital":
        return 'linear-gradient(to bottom, #5c8a1c, #3a6c2b, #245137)'; // Mid growth
      case "print":
        return 'linear-gradient(to bottom, #84a72b, #5c8a1c, #3a6c2b)'; // Light/Growth
      default:
        return 'linear-gradient(to bottom, #245137, #0e3030)';
    }
  };

  return (
    <div className="min-h-screen" style={{background: getStageBackground()}}>
      {/* Navigation between stages */}
      <div className="sticky top-0 z-50 backdrop-blur-sm border-b" 
           style={{backgroundColor: 'rgba(14, 48, 48, 0.8)', borderColor: 'rgba(92, 138, 28, 0.2)'}}>
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex justify-center gap-2 md:gap-4">
            <button
              onClick={() => setActiveStage("seed")}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all`}
              style={{
                backgroundColor: activeStage === "seed" ? 'rgba(92, 138, 28, 0.3)' : 'transparent',
                color: activeStage === "seed" ? '#a9c24d' : '#5c8a1c',
                border: activeStage === "seed" ? '1px solid rgba(132, 167, 43, 0.5)' : '1px solid transparent'
              }}
            >
              <Leaf className="w-4 h-4" />
              <span className="hidden md:inline">PLANT</span>
              <span className="md:hidden">1</span>
            </button>
            <button
              onClick={() => setActiveStage("digital")}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all`}
              style={{
                backgroundColor: activeStage === "digital" ? 'rgba(132, 167, 43, 0.3)' : 'transparent',
                color: activeStage === "digital" ? '#cddc7a' : '#5c8a1c',
                border: activeStage === "digital" ? '1px solid rgba(169, 194, 77, 0.5)' : '1px solid transparent'
              }}
            >
              <Sprout className="w-4 h-4" />
              <span className="hidden md:inline">WATER</span>
              <span className="md:hidden">2</span>
            </button>
            <button
              onClick={() => setActiveStage("print")}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all`}
              style={{
                backgroundColor: activeStage === "print" ? 'rgba(169, 194, 77, 0.3)' : 'transparent',
                color: activeStage === "print" ? '#edf5ac' : '#5c8a1c',
                border: activeStage === "print" ? '1px solid rgba(205, 220, 122, 0.5)' : '1px solid transparent'
              }}
            >
              <TreeDeciduous className="w-4 h-4" />
              <span className="hidden md:inline">GROW</span>
              <span className="md:hidden">3</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {activeStage === "seed" && <SeedStage />}
        {activeStage === "digital" && <DigitalStage />}
        {activeStage === "print" && <PrintStage />}
      </div>

      {/* Social Proof Section - Gradient to lighter */}
      <div className="py-16" style={{background: 'linear-gradient(to bottom, transparent, rgba(58, 108, 43, 0.3))'}}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Why This Journey Matters</h2>
            <p className="max-w-2xl mx-auto" style={{color: '#84a72b'}}>
              Created by Keeper Of The Fiyah for wise and capable women 
              seeking divine connection with the Almighty Creator.
            </p>
          </div>

          {/* Founder Story - Social Proof for New Product */}
          <div className="rounded-2xl p-8 mb-8 border"
               style={{
                 backgroundColor: 'rgba(36, 81, 55, 0.4)',
                 borderColor: 'rgba(92, 138, 28, 0.3)'
               }}>
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0"
                   style={{backgroundColor: 'rgba(132, 167, 43, 0.3)'}}>
                <Heart className="w-8 h-8" style={{color: '#a9c24d'}} />
              </div>
              <div>
                <p className="italic mb-4" style={{color: '#cddc7a'}}>
                  "These pages were curated with intention - each design, each affirmation, 
                  each sacred pattern is meant to orient your spirit toward divine connection. 
                  When you color, you meditate. When you meditate, you connect. 
                  This is YAHFFIRMATIONS in action."
                </p>
                <p className="font-semibold" style={{color: '#a9c24d'}}>— Keeper Of The Fiyah</p>
                <p className="text-sm" style={{color: '#5c8a1c'}}>Creator, INSPIYAH | A.R.T. Energi</p>
              </div>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Heart, label: "Made with Love", sublabel: "Curated with Intention" },
              { icon: Sparkles, label: "Divine Designs", sublabel: "Sacred Patterns" },
              { icon: BookOpen, label: "YAHFFIRMATIONS", sublabel: "Scripture-Based" },
              { icon: Star, label: "Premium Quality", sublabel: "Print & Digital" }
            ].map((item, idx) => (
              <div key={idx} className="text-center p-4">
                <item.icon className="w-8 h-8 mx-auto mb-2" style={{color: '#84a72b'}} />
                <p className="text-sm font-semibold text-white">{item.label}</p>
                <p className="text-xs" style={{color: '#5c8a1c'}}>{item.sublabel}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="py-8 border-t" style={{backgroundColor: '#0e3030', borderColor: 'rgba(58, 108, 43, 0.3)'}}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="font-semibold mb-2" style={{color: '#a9c24d'}}>A.R.T. Energi</p>
          <p className="text-sm mb-4" style={{color: '#5c8a1c'}}>Authenticity | Reliability | Truth</p>
          <p className="text-xs" style={{color: '#245137'}}>
            YAH HEALTH WEALTH HAPPINESS - The Universal Pillars Of Success
          </p>
          <p className="text-xs mt-2" style={{color: '#245137'}}>
            © {new Date().getFullYear()} INSPIYAH | Keeper Of The Fiyah. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ColoringBookFunnel;
